import React, { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { logError } from "../lib/log";
import { ShieldBan, Plus, X, RotateCcw } from "lucide-react";

function useClassFilter() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/class/filter").then((r) => setF(r.data)).catch((e) => logError("Filter load failed", e)); }, []);
  const save = async (added, removed) => {
    setSaving(true);
    setErr(null);
    try {
      const { data } = await api.put("/class/filter", { added, removed });
      setF(data);
    } catch (e) {
      setErr(apiError(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };
  return { f, saving, err, save };
}

function DefaultWords({ f, onToggle }) {
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {f.default.map((w) => {
        const off = f.removed.includes(w);
        return (
          <button key={w} onClick={() => onToggle(w)} data-testid={`default-word-${w}`} aria-pressed={!off} title={off ? "Switched off — tap to block again" : "Blocked — tap to allow"}
            className={`rounded-full border px-3 py-1 font-mono-num text-xs transition-colors ${off ? "border-white/10 text-slate-500 line-through" : "border-red-400/40 bg-red-500/10 text-red-200"}`}>
            {w}
          </button>
        );
      })}
    </div>
  );
}

function CustomWords({ f, onRemove }) {
  if (f.added.length === 0) return <div className="mt-2 text-xs text-slate-500">None yet.</div>;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {f.added.map((w) => (
        <span key={w} data-testid={`custom-word-${w}`} className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 font-mono-num text-xs text-amber-100">
          {w}
          <button onClick={() => onRemove(w)} data-testid={`remove-word-${w}`} className="icon-btn rounded-full p-0.5 hover:bg-white/10"><X size={10} /></button>
        </span>
      ))}
    </div>
  );
}

export function BlockedWordsEditor() {
  const { f, saving, err, save } = useClassFilter();
  const [word, setWord] = useState("");
  if (!f) return null;

  const toggleDefault = (w) => save(f.added, f.removed.includes(w) ? f.removed.filter((x) => x !== w) : [...f.removed, w]);
  const addWord = () => {
    const w = word.trim().toLowerCase();
    setWord("");
    if (!w || f.added.includes(w)) return;
    if (f.default.includes(w)) return f.removed.includes(w) ? toggleDefault(w) : undefined;
    return save([...f.added, w], f.removed);
  };
  const dirty = f.added.length > 0 || f.removed.length > 0;

  return (
    <div className="rounded-md border border-red-400/30 bg-red-500/5 p-4 sm:p-5" data-testid="blocked-words-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-red-300"><ShieldBan size={12} /> Class chat word filter</div>
          <div className="font-display mt-1 text-lg font-semibold text-white">Blocked words · <span className="font-mono-num text-red-200" data-testid="blocked-words-count">{f.effective.length}</span> active</div>
        </div>
        {saving && <span className="text-xs text-slate-400">Saving…</span>}
      </div>
      <p className="mt-1 text-xs text-slate-400">Tap a default word to switch it off for your class. Add Slovak or age-specific words below — they're censored with **** in the chat.</p>

      <div className="mt-4 text-[10px] uppercase tracking-widest text-slate-500">Default list</div>
      <DefaultWords f={f} onToggle={toggleDefault} />

      <div className="mt-4 text-[10px] uppercase tracking-widest text-slate-500">Your additions</div>
      <CustomWords f={f} onRemove={(w) => save(f.added.filter((x) => x !== w), f.removed)} />

      <div className="mt-3 flex gap-2">
        <input value={word} onChange={(e) => setWord(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addWord()} placeholder="Add a word…" data-testid="blocked-word-input"
          className="min-w-0 flex-1 rounded-md border border-white/10 bg-black/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-red-400/60 focus:outline-none" />
        <button onClick={addWord} disabled={!word.trim() || saving} data-testid="blocked-word-add" className="inline-flex items-center gap-1 rounded-md border border-red-400/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-100 hover:bg-red-500/20 disabled:opacity-50"><Plus size={12} /> Add</button>
        {dirty && <button onClick={() => save([], [])} data-testid="blocked-words-reset" title="Reset to defaults" className="rounded-md border border-white/10 px-2 text-slate-400 hover:text-slate-100"><RotateCcw size={12} /></button>}
      </div>
      {err && <div className="mt-2 text-xs text-red-300" data-testid="blocked-words-error">{err}</div>}
    </div>
  );
}
